/**
 * Portal Header Component
 * Top header with breadcrumbs, notifications, and user menu
 */

import { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { 
  Menu, 
  Bell, 
  ChevronDown, 
  LogOut,
  Settings,
  HelpCircle,
  Home,
  ChevronRight
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../lib/api';

interface PortalHeaderProps {
  onToggleSidebar: () => void;
}

export function PortalHeader({ onToggleSidebar }: PortalHeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  // Generate breadcrumbs from path
  const getBreadcrumbs = () => {
    const paths = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: { label: string; path: string }[] = [];
    
    let currentPath = '';
    for (const segment of paths) {
      currentPath += `/${segment}`;
      
      // Map path segments to labels
      let label = segment;
      if (segment === 'projects') label = 'Projects';
      else if (segment === 'settings') label = 'Settings';
      else if (segment === 'map') label = 'Map';
      else if (segment === 'photos') label = 'Photos';
      else if (segment === 'anomalies') label = 'Anomalies';
      else if (segment === 'report') label = 'Report';
      
      breadcrumbs.push({ label, path: currentPath });
    }
    
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  const handleLogout = async () => {
    try {
      // Logout call uses httpOnly cookie for refresh token
      await authApi.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      logout();
      navigate('/login');
    }
  };

  return (
    <header className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700/50 px-4 py-3 flex items-center justify-between relative z-50 transition-colors">
      {/* Left side - menu toggle and breadcrumbs */}
      <div className="flex items-center gap-4">
        <button
          onClick={onToggleSidebar}
          className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-lg transition-colors lg:hidden"
          title="Toggle menu"
        >
          <Menu className="w-5 h-5 text-slate-500 dark:text-slate-400" />
        </button>

        {/* Breadcrumbs */}
        <nav className="flex items-center gap-1 text-sm font-mono">
          <Link 
            to="/" 
            className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
          >
            <Home className="w-4 h-4" />
          </Link>
          {breadcrumbs.map((crumb, index) => (
            <div key={crumb.path} className="flex items-center gap-1">
              <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-600" />
              {index === breadcrumbs.length - 1 ? (
                <span className="font-medium text-slate-800 dark:text-slate-100 tracking-wide uppercase text-xs">{crumb.label}</span>
              ) : (
                <Link 
                  to={crumb.path}
                  className="text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors uppercase text-xs tracking-wide"
                >
                  {crumb.label}
                </Link>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Right side - notifications and user */}
      <div className="flex items-center gap-2">
        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-lg transition-colors relative"
            title="Notifications"
          >
            <Bell className="w-5 h-5 text-slate-500 dark:text-slate-400" />
            {/* Notification badge */}
            <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
          </button>

          {notificationsOpen && (
            <>
              <div 
                className="fixed inset-0 z-[100]" 
                onClick={() => setNotificationsOpen(false)} 
              />
              <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-slate-800/95 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200 dark:border-slate-700/50 z-[101]">
                <div className="p-4 border-b border-slate-200 dark:border-slate-700/50">
                  <h3 className="font-mono font-semibold text-slate-800 dark:text-slate-100 text-xs uppercase tracking-wider">Notifications</h3>
                </div>
                <div className="p-4 text-center text-slate-500 text-sm font-mono">
                  No active alerts
                </div>
              </div>
            </>
          )}
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-lg transition-colors"
          >
            <div className="w-8 h-8 bg-blue-600 dark:bg-blue-600/80 rounded-full flex items-center justify-center border border-blue-500 dark:border-blue-500/50">
              <span className="text-white font-mono font-medium text-sm">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <span className="font-mono font-medium text-slate-700 dark:text-slate-300 hidden sm:block text-sm">
              {user?.name || 'User'}
            </span>
            <ChevronDown className="w-4 h-4 text-slate-500" />
          </button>

          {userMenuOpen && (
            <>
              <div 
                className="fixed inset-0 z-[100]" 
                onClick={() => setUserMenuOpen(false)} 
              />
              <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-slate-800/95 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200 dark:border-slate-700/50 z-[101] py-2">
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700/50">
                  <p className="font-mono font-medium text-slate-800 dark:text-slate-100">{user?.name || 'User'}</p>
                  <p className="text-sm text-slate-500 font-mono">{user?.email || ''}</p>
                  {user?.role && (
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs font-mono font-medium bg-blue-100 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 rounded border border-blue-200 dark:border-blue-500/30">
                      {user.role}
                    </span>
                  )}
                </div>
                
                <Link
                  to="/settings"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span className="font-mono text-sm">Settings</span>
                </Link>
                
                <button
                  className="w-full flex items-center gap-3 px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
                >
                  <HelpCircle className="w-4 h-4" />
                  <span className="font-mono text-sm">Help</span>
                </button>
                
                <div className="border-t border-slate-200 dark:border-slate-700/50 mt-2 pt-2">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="font-mono text-sm">Sign Out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
